import { useEffect, useRef, type RefObject } from "react";

const modalStack: symbol[] = [];
let bodyLockCount = 0;
let previousBodyOverflow = "";

const FOCUSABLE_SELECTOR = [
	"button:not([disabled])",
	"[href]",
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	'[tabindex]:not([tabindex="-1"])',
].join(",");

const focusableElements = (root: HTMLElement): HTMLElement[] =>
	Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
		element => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true",
	);

/**
 * One layer of the Escape stack, and nothing else.
 *
 * Every dismissible layer on screen registers here, and the one registered last answers Escape —
 * so "which of these closes" is decided by the order they opened rather than by an if/else chain
 * that has to reason about z-order, and by the capture phase rather than by luck. Split out of
 * `useModalAccessibility` because the layers that need the ordering are not all dialogs: a
 * fullscreen image or a nested Bible modal owns no card of its own to trap focus in, and must not
 * take the body scroll lock away from the modal it opened over.
 */
export const useEscapeLayer = (open: boolean, onEscape: () => void): { isTop: () => boolean } => {
	const onEscapeRef = useRef(onEscape);
	onEscapeRef.current = onEscape;
	const tokenRef = useRef<symbol | null>(null);

	useEffect(() => {
		if (!open) return;
		const token = Symbol("escape-layer");
		tokenRef.current = token;
		modalStack.push(token);

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || modalStack.at(-1) !== token) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			onEscapeRef.current();
		};

		document.addEventListener("keydown", onKeyDown, true);

		return () => {
			document.removeEventListener("keydown", onKeyDown, true);
			const index = modalStack.lastIndexOf(token);
			if (index >= 0) modalStack.splice(index, 1);
			tokenRef.current = null;
		};
	}, [open]);

	return { isTop: () => tokenRef.current !== null && modalStack.at(-1) === tokenRef.current };
};

/** Shared modal behavior: top-layer Escape, focus trap/restore, and body scroll lock. */
export const useModalAccessibility = (
	open: boolean,
	onEscape: () => void,
): RefObject<HTMLDivElement | null> => {
	const dialogRef = useRef<HTMLDivElement>(null);
	const { isTop } = useEscapeLayer(open, onEscape);

	useEffect(() => {
		if (!open) return;
		const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

		if (bodyLockCount === 0) {
			previousBodyOverflow = document.body.style.overflow;
			document.body.style.overflow = "hidden";
		}
		bodyLockCount += 1;

		const frame = window.requestAnimationFrame(() => {
			const dialog = dialogRef.current;
			if (!dialog || !isTop()) return;
			(focusableElements(dialog)[0] ?? dialog).focus();
		});

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Tab" || !isTop()) return;
			const dialog = dialogRef.current;
			if (!dialog) return;
			const focusable = focusableElements(dialog);
			if (focusable.length === 0) {
				event.preventDefault();
				dialog.focus();
				return;
			}
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKeyDown, true);
		return () => {
			window.cancelAnimationFrame(frame);
			document.removeEventListener("keydown", onKeyDown, true);
			bodyLockCount = Math.max(0, bodyLockCount - 1);
			if (bodyLockCount === 0) document.body.style.overflow = previousBodyOverflow;
			if (opener?.isConnected) opener.focus();
		};
		// `isTop` reads a ref, so it is stable in effect terms and is deliberately not a dependency.
	}, [open]);

	return dialogRef;
};
